import { WalkEndpoint } from '@/core/network/api/constants/api.url.constant';
import type { MapMarkerData } from '@/core/network/api/map-puzzle/map-puzzle.api';
import type { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import type { AxiosResponse } from 'axios';

export interface WalkPuzzleData {
  roundId: string;
  roundNumber: number;
  fen: string;
  setupMove: string;
  solutionMove: string;
  rating: number;
}

export interface WalkSessionState {
  id: string;
  status: 'active' | 'completed' | 'timed_out' | 'revealed' | 'walking_reds' | 'finished';
  currentPhase: 'idle' | 'warmup' | 'thinking' | 'break';
  phaseStartedAt: string | null;
  timeRemainingMs: number;
  breakTimeRemainingMs: number;
  currentRound: number;
  totalRounds: number;
  timeBankMs: number;
  breakEvenSpeedMps: number;
  puzzleRating: number;
  totalDistanceM: number;
  startLocationId: string | null;
  revealLocationId: string | null;
  secondRevealLocationId: string | null;
  originLat: number;
  originLng: number;
  revealLat: number | null;
  revealLng: number | null;
  secondRevealLat: number | null;
  secondRevealLng: number | null;
}

export interface WalkRoundSummary {
  id: string;
  roundNumber: number;
  isCorrect: boolean | null;
  moveSubmittedAt: string | null;
}

export interface WalkSessionResponse extends WalkSessionState {
  nextPuzzle: WalkPuzzleData | null;
  allPuzzles: WalkPuzzleData[];
  rounds: WalkRoundSummary[];
  startMarker?: MapMarkerData;
  redWalkPuzzles?: WalkPuzzleData[];
}

export interface StartThinkResponse {
  timedOut: boolean;
  session: WalkSessionState;
  puzzle?: WalkPuzzleData;
}

export interface SubmitMoveResponse {
  isCorrect?: boolean;
  isLastRound?: boolean;
  isRedWalk?: boolean;
  session: WalkSessionState;
}

export interface PuzzleTimeoutResponse {
  isLastRound?: boolean;
  session: WalkSessionState;
}

export interface RevealResultPuzzle {
  roundNumber: number;
  puzzleId: number;
  fen: string;
  setupMove: string;
  solutionMove: string;
  rating: number;
  isCorrect: boolean | null;
  submitLat: number | null;
  submitLng: number | null;
  submittedMoveUci: string | null;
  traceId: string | null;
}

export interface RevealResponse {
  session: WalkSessionState;
  revealResults?: {
    solved: number;
    submitted: number;
    puzzles: RevealResultPuzzle[];
  };
  hasWrongRounds?: boolean;
  wrongCount?: number;
  redWalkResults?: {
    walked: number;
    corrected: number;
    remaining: number;
  };
  startSpotName?: string | null;
  revealSpotName?: string | null;
  totalDurationMs?: number;
  markers?: MapMarkerData[];
  markerExpiresAt?: string | null;
}

export interface WalkRedsResponse {
  session: WalkSessionState;
  redWalkPuzzles: WalkPuzzleData[];
}

export interface FinishSessionResponse {
  session: WalkSessionState;
  markers?: MapMarkerData[];
}

export interface SyncAction {
  id: string;
  type: 'startThink' | 'submitMove' | 'puzzleTimeout';
  lat: number;
  lng: number;
  distanceM: number;
  timeRemainingMs?: number;
  breakTimeRemainingMs?: number;
  moveUci?: string;
  timestamp: number;
}

export interface SyncSessionResponse {
  session: WalkSessionState;
  processedIds: string[];
}

export interface SessionSummaryResponse {
  sessionId: string;
  status: string;
  puzzleRating: number;
  timeBankMs: number;
  breakBankMs: number;
  breakEvenSpeedMps: number;
  totalSolved: number;
  totalSubmitted: number;
  ratingMin: number | null;
  ratingMax: number | null;
  totalDurationMs: number | null;
  warmUpMs: number;
  totalPuzzleTimeMs: number;
  avgPuzzleSpeedMps: number;
  maxPuzzleSpeedMps: number;
  maxPuzzleSpeedRating: number | null;
  longestStreak: number;
  longestStreakAvgSpeedMps: number;
  longestStreakDurationMs: number;
  totalDistanceM: number;
  startSpotName: string | null;
  revealSpotName: string | null;
  redWalkCorrected: number;
  redWalkTotal: number;
  originLat: number;
  originLng: number;
  revealLat: number | null;
  revealLng: number | null;
  rounds: {
    roundNumber: number;
    isCorrect: boolean | null;
    submitted: boolean;
    redWalkIsCorrect: boolean | null;
    lat: number | null;
    lng: number | null;
    thinkMs: number;
    breakMs: number;
    sideToMove: 'w' | 'b';
  }[];
}

export interface WalkDailySegment {
  sessionId: string;
  total: number;
  solved: number;
  puzzleRatingMin: number | null;
  puzzleRatingMax: number | null;
  puzzleRating: number;
  timeBankMs: number;
  breakEvenSpeedMps: number;
  totalDurationMs: number | null;
  avgPuzzleSpeedMps: number;
  maxPuzzleSpeedMps: number;
  maxPuzzleSpeedRating: number | null;
  puzzleResults: ('correct' | 'wrong' | 'corrected' | 'not_submitted')[];
}

export interface WalkDailyCard {
  date: string;
  spotted: number;
  solved: number;
  notSubmitted: number;
  reveals: number;
  totalDistanceM: number;
  segments: WalkDailySegment[];
}

export interface WalkMonthCard {
  month: string;
  spotted: number;
  solved: number;
  notSubmitted: number;
  reveals: number;
  totalDistanceM: number;
}

export interface WalkHistory {
  days: WalkDailyCard[];
  months: WalkMonthCard[];
}

export interface TraceTotals {
  traceMine: number;
  traceOthers: number;
}

export interface StreakRecord {
  field: string;
  band: string;
  speedKmh: number;
  timeBankMs: number;
  streakLength: number;
  durationMs: number;
  isVariant: boolean;
  sessionId: string;
  sessionDate: string;
}

export interface FieldSessionData {
  sessionId: string;
  sessionDate: string;
  totalSolved: number;
  totalRounds: number;
  totalDurationMs: number;
  successPct: number;
  accuracyPct: number;
}

export interface FieldStats {
  field: string;
  band: string;
  speedKmh: number;
  totalSessions: number;
  approvedCount: number;
  longestStreak: number;
  totalDurationMs: number;
  totalDistanceM: number;
  topSessions: FieldSessionData[];
}

interface CreateSessionBody {
  puzzleRatingBand: string;
  timeBankMs: number;
  breakEvenSpeedMps: number;
  originLat: number;
  originLng: number;
  breakBankMs?: number;
  startLocationId?: string;
  mapTracesEnabled?: boolean;
}

interface StartThinkBody {
  lat: number;
  lng: number;
  timeRemainingMs: number;
  breakTimeRemainingMs: number;
  distanceM: number;
}

interface PuzzleTimeoutBody {
  lat: number;
  lng: number;
  distanceM: number;
}

interface SubmitMoveBody {
  moveUci: string;
  lat: number;
  lng: number;
  timeRemainingMs: number;
  distanceM: number;
}

interface RevealBody {
  lat: number;
  lng: number;
  revealLocationId?: string;
  distanceM?: number;
}

export class WalkApi {
  private readonly http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  async createSession(
    puzzleRatingBand: string,
    timeBankMs: number,
    breakEvenSpeedMps: number,
    originLat: number,
    originLng: number,
    startLocationId?: string,
    breakBankMs?: number,
    mapTracesEnabled?: boolean,
  ): Promise<AxiosResponse<GamResponse<WalkSessionResponse>>> {
    return this.http.post<WalkSessionResponse, CreateSessionBody>(WalkEndpoint.CREATE, {
      data: {
        puzzleRatingBand,
        timeBankMs,
        breakEvenSpeedMps,
        originLat,
        originLng,
        startLocationId,
        breakBankMs,
        mapTracesEnabled,
      },
    });
  }

  async getActiveSession(): Promise<AxiosResponse<GamResponse<WalkSessionResponse | null>>> {
    return this.http.get<WalkSessionResponse | null>(WalkEndpoint.ACTIVE);
  }

  async startThink(
    sessionId: string,
    lat: number,
    lng: number,
    timeRemainingMs: number,
    breakTimeRemainingMs: number,
    distanceM: number,
  ): Promise<AxiosResponse<GamResponse<StartThinkResponse>>> {
    return this.http.post<StartThinkResponse, StartThinkBody>(WalkEndpoint.START_THINK, {
      urlParams: { sessionId },
      data: { lat, lng, timeRemainingMs, breakTimeRemainingMs, distanceM },
    });
  }

  async puzzleTimeout(
    sessionId: string,
    lat: number,
    lng: number,
    distanceM: number,
  ): Promise<AxiosResponse<GamResponse<PuzzleTimeoutResponse>>> {
    return this.http.post<PuzzleTimeoutResponse, PuzzleTimeoutBody>(WalkEndpoint.PUZZLE_TIMEOUT, {
      urlParams: { sessionId },
      data: { lat, lng, distanceM },
    });
  }

  async submitMove(
    sessionId: string,
    moveUci: string,
    lat: number,
    lng: number,
    timeRemainingMs: number,
    distanceM: number,
  ): Promise<AxiosResponse<GamResponse<SubmitMoveResponse>>> {
    return this.http.post<SubmitMoveResponse, SubmitMoveBody>(WalkEndpoint.SUBMIT_MOVE, {
      urlParams: { sessionId },
      data: { moveUci, lat, lng, timeRemainingMs, distanceM },
    });
  }

  async reveal(
    sessionId: string,
    lat: number,
    lng: number,
    revealLocationId?: string,
    distanceM?: number,
  ): Promise<AxiosResponse<GamResponse<RevealResponse>>> {
    return this.http.post<RevealResponse, RevealBody>(WalkEndpoint.REVEAL, {
      urlParams: { sessionId },
      data: { lat, lng, revealLocationId, distanceM },
    });
  }

  async walkReds(sessionId: string): Promise<AxiosResponse<GamResponse<WalkRedsResponse>>> {
    return this.http.post<WalkRedsResponse, Record<string, never>>(WalkEndpoint.WALK_REDS, {
      urlParams: { sessionId },
      data: {},
    });
  }

  async finishSession(sessionId: string): Promise<AxiosResponse<GamResponse<FinishSessionResponse>>> {
    return this.http.post<FinishSessionResponse, Record<string, never>>(WalkEndpoint.FINISH, {
      urlParams: { sessionId },
      data: {},
    });
  }

  async updateSpots(
    sessionId: string,
    startLocationId?: string,
    revealLocationId?: string,
  ): Promise<AxiosResponse<GamResponse<{ success: boolean }>>> {
    return this.http.patch<{ success: boolean }, { startLocationId?: string; revealLocationId?: string }>(
      WalkEndpoint.UPDATE_SPOTS,
      {
        urlParams: { sessionId },
        data: { startLocationId, revealLocationId },
      },
    );
  }

  async getSummary(sessionId: string): Promise<AxiosResponse<GamResponse<SessionSummaryResponse>>> {
    return this.http.get<SessionSummaryResponse>(WalkEndpoint.SUMMARY, {
      urlParams: { sessionId },
    });
  }

  async getDailyCards(limit = 7): Promise<AxiosResponse<GamResponse<WalkDailyCard[]>>> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http.get<WalkDailyCard[]>(WalkEndpoint.DAILY_CARDS, {
      queryParams: { limit: String(limit), timezone },
    });
  }

  async getWalkHistory(): Promise<AxiosResponse<GamResponse<WalkHistory>>> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return this.http.get<WalkHistory>(WalkEndpoint.HISTORY, {
      queryParams: { timezone },
    });
  }

  async getStreakRecords(): Promise<AxiosResponse<GamResponse<StreakRecord[]>>> {
    return this.http.get<StreakRecord[]>(WalkEndpoint.STREAK_RECORDS);
  }

  async getFieldStats(): Promise<AxiosResponse<GamResponse<FieldStats[]>>> {
    return this.http.get<FieldStats[]>(WalkEndpoint.FIELD_STATS);
  }

  async getTraceTotals(): Promise<AxiosResponse<GamResponse<TraceTotals>>> {
    return this.http.get<TraceTotals>(WalkEndpoint.TRACE_TOTALS);
  }

  async syncSession(
    sessionId: string,
    actions: SyncAction[],
  ): Promise<AxiosResponse<GamResponse<SyncSessionResponse>>> {
    return this.http.post<SyncSessionResponse, { actions: SyncAction[] }>(WalkEndpoint.SYNC, {
      urlParams: { sessionId },
      data: { actions },
    });
  }

  async deleteSession(sessionId: string): Promise<AxiosResponse<GamResponse<{ success: boolean }>>> {
    return this.http.delete<{ success: boolean }>(WalkEndpoint.DELETE, {
      urlParams: { sessionId },
    });
  }
}
